/* eslint-disable @typescript-eslint/no-explicit-any */
// Widget Registry - Defines all available widgets for customizable dashboard
// Comprehensive widget definitions across all platform modules

import { HEALTH_WIDGETS } from './widgets/healthWidgets';
import { HUB_REGISTRY_WIDGETS } from './widgets/hubRegistryWidgets';
import { PLATFORMOPS_REGISTRY_WIDGETS } from './widgets/platformopsRegistryWidgets';
import { DATAOPS_REGISTRY_WIDGETS } from './widgets/dataopsRegistryWidgets';
import { GENAIOPS_REGISTRY_WIDGETS } from './widgets/genaiopsRegistryWidgets';
import { AGENTOPS_REGISTRY_WIDGETS } from './widgets/agentopsRegistryWidgets';
import {
  REGISTRY_WIDGET_CATEGORIES,
  REGISTRY_WIDGET_SIZES,
  REGISTRY_WIDGET_TYPES,
  REGISTRY_ICONS,
} from './widgets/widgetRegistryConstants';

// Re-export constants under their canonical names for backwards compatibility
export const WIDGET_CATEGORIES = REGISTRY_WIDGET_CATEGORIES;
export const WIDGET_SIZES = REGISTRY_WIDGET_SIZES;
export const WIDGET_TYPES = REGISTRY_WIDGET_TYPES;

// Empty state configurations by data source
export const EMPTY_STATE_CONFIGS = {
  hub: {
    title: 'No Projects Yet',
    instructions: [
      'Create your first project from the Projects page',
      'Add team members and configure milestones',
      'Track your project progress here'
    ],
    docsUrl: '/hub/projects',
    actionLabel: 'Create Project',
    actionPath: '/hub/projects'
  },
  genaiops: {
    title: 'No LLM Data Available',
    instructions: [
      'Install the Aivilo SDK in your application',
      'Initialize tracing with your API key',
      'Run your first LLM call to start tracking'
    ],
    actionLabel: 'View SDK Setup',
    actionPath: '/ai-operations/sdk'
  },
  agentops: {
    title: 'No Agent Data Available',
    instructions: [
      'Install the Aivilo SDK in your application',
      'Configure agent tracking with your API key',
      'Run your first agent to start collecting data'
    ],
    actionLabel: 'View SDK Setup',
    actionPath: '/ai-operations/sdk'
  },
  'ai-operations': {
    title: 'No AI Operations Data',
    instructions: [
      'Install the Aivilo SDK in your application',
      'Initialize tracing with your API key',
      'Run your first LLM call or agent to start tracking'
    ],
    actionLabel: 'View SDK Setup',
    actionPath: '/ai-operations/sdk'
  },
  dataops: {
    title: 'No Data Pipeline Data',
    instructions: [
      'Connect your data catalog (dbt, Airflow, etc.)',
      'Configure data lineage tracking',
      'Monitor your pipeline runs'
    ],
    actionLabel: 'View Catalog',
    actionPath: '/dataops/catalog'
  },
  platformops: {
    title: 'No Cloud Data Available',
    instructions: [
      'Connect your AWS account to Aivilo',
      'Enable Cost Explorer and resource tracking',
      'Configure compliance policies'
    ],
    actionLabel: 'Connect AWS',
    actionPath: '/hub/aws-environments'
  }
};

// Widget definitions - Comprehensive set across all modules
// Entries are split into category-specific files to keep this file manageable.
export const WIDGET_REGISTRY = [
  ...HUB_REGISTRY_WIDGETS,
  ...PLATFORMOPS_REGISTRY_WIDGETS,
  ...DATAOPS_REGISTRY_WIDGETS,
  ...GENAIOPS_REGISTRY_WIDGETS,
  ...AGENTOPS_REGISTRY_WIDGETS,

  // CUSTOM
  {
    id: 'custom-metric',
    name: 'Custom Metric',
    category: WIDGET_CATEGORIES.CUSTOM,
    type: WIDGET_TYPES.CUSTOM,
    defaultSize: WIDGET_SIZES.SMALL,
    description: 'User-defined metric',
    icon: REGISTRY_ICONS.FiStar,
    dataSource: 'custom',
    refreshRate: 60000,
    supportsDateFilter: false
  },

  // HEALTH WIDGETS (imported from healthWidgets.ts)
  ...HEALTH_WIDGETS
];

// Helper functions
export const getWidgetById = (id: string) => {
  return WIDGET_REGISTRY.find(w => w.id === id);
};

export const getWidgetsByCategory = (category: string) => {
  return WIDGET_REGISTRY.filter(w => w.category === category);
};

export const getDefaultLayout = (role: string) => {
  // Default layouts based on user role - optimized for 4-column grid
  const layouts = {
    client: [
      { i: 'active-projects', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-budget', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-spent', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'burn-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'monthly-spend-trend', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'project-status-distribution', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'recent-projects', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'hours-by-person', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM }
    ],
    partner: [
      { i: 'platform-health', x: 0, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'total-cloud-spend', x: 2, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'active-projects', x: 0, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'compliance-score', x: 1, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'open-violations', x: 2, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'unread-notifications', x: 3, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'cost-by-service', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'estimated-savings', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM }
    ],
    aws: [
      { i: 'active-projects', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-budget', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-spent', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'burn-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'monthly-spend-trend', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'project-status-distribution', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM }
    ]
  };

  return (layouts as Record<string, typeof layouts.client>)[role] || layouts.client;
};

export const PRESET_LAYOUTS = {
  commandCenter: {
    name: 'Command Center',
    description: 'Executive view of all platform modules',
    isDefault: true,
    widgets: [
      // Row 1: Executive KPIs
      { i: 'system-health-score', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'unified-total-cost', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-active-operations', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'platform-error-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      // Row 2: Module Status Grid (full width)
      { i: 'module-status-grid', x: 0, y: 1, ...WIDGET_SIZES.WIDE },
      // Row 3: Charts and Alerts
      { i: 'llm-cost-trend', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'cross-platform-alerts', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM },
      // Row 4: Operational Metrics
      { i: 'agent-activity-summary', x: 0, y: 5, ...WIDGET_SIZES.SMALL },
      { i: 'pipeline-health', x: 1, y: 5, ...WIDGET_SIZES.SMALL },
      { i: 'cloud-compliance-summary', x: 2, y: 5, ...WIDGET_SIZES.SMALL },
      { i: 'active-projects', x: 3, y: 5, ...WIDGET_SIZES.SMALL }
    ]
  },
  general: {
    name: 'General',
    description: 'Default dashboard layout with essential metrics',
    widgets: [
      { i: 'active-projects', x: 0, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-budget', x: 1, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'total-spent', x: 2, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'burn-rate', x: 3, y: 0, ...WIDGET_SIZES.SMALL },
      { i: 'monthly-spend-trend', x: 0, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'project-status-distribution', x: 2, y: 1, ...WIDGET_SIZES.MEDIUM },
      { i: 'recent-projects', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'hours-by-person', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  executive: {
    name: 'Executive View',
    description: 'High-level metrics and strategic insights',
    widgets: [
      { i: 'platform-health', x: 0, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'total-cloud-spend', x: 2, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'total-budget', x: 0, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'total-spent', x: 1, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'estimated-savings', x: 2, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'compliance-score', x: 3, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'monthly-spend-trend', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'project-status-distribution', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  technical: {
    name: 'Technical View',
    description: 'Operational metrics and system health',
    widgets: [
      { i: 'platform-health', x: 0, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'compliance-score', x: 2, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'active-environments', x: 0, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'open-violations', x: 1, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'data-quality-score', x: 2, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'agent-success-rate', x: 3, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'cost-by-service', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'traces-over-time', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM }
    ]
  },
  financial: {
    name: 'Financial View',
    description: 'Cost tracking and budget management',
    widgets: [
      { i: 'total-cloud-spend', x: 0, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'estimated-savings', x: 2, y: 0, ...WIDGET_SIZES.MEDIUM },
      { i: 'total-budget', x: 0, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'total-spent', x: 1, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'burn-rate', x: 2, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'total-llm-cost', x: 3, y: 2, ...WIDGET_SIZES.SMALL },
      { i: 'cost-by-service', x: 0, y: 3, ...WIDGET_SIZES.MEDIUM },
      { i: 'monthly-spend-trend', x: 2, y: 3, ...WIDGET_SIZES.MEDIUM }
    ]
  }
};

// Helper to get empty state config for a widget
export const getEmptyStateForWidget = (widgetId: string) => {
  const widget = getWidgetById(widgetId);
  if (!widget) return null;

  // First check if widget has specific emptyStateConfig
  const w = widget as Record<string, unknown>;
  if (w.emptyStateConfig) {
    return (EMPTY_STATE_CONFIGS as Record<string, unknown>)[w.emptyStateConfig as string] || null;
  }

  // Fall back to dataSource-based config
  return (EMPTY_STATE_CONFIGS as Record<string, unknown>)[widget.dataSource] || null;
};

// Re-export all dashboard templates and helper functions
export * from './dashboardTemplates';
